/** Authored service-specific checks in fixed order. No template rotation or deadline promises. */
import type { LgzLocale } from "@/data/lgz/locale-copy";
export type LocaleServiceGuidance = { documents: string; action: string; boundary: string };
export const LGZ_LOCALE_GUIDANCE: Record<LgzLocale, Record<string, LocaleServiceGuidance>> = {
  "en": {
    "notary-signature": {
      "documents": "Present original ID and an unsigned, complete document.",
      "action": "The signatory appears and signs before the certifying attorney, using the wording required by the recipient.",
      "boundary": "Certification witnesses the signing; it does not prove the truth or legal validity of the document."
    },
    "notary-true-copy": {
      "documents": "Present the original and a clear copy of every relevant page.",
      "action": "Compare the copy with the original; first confirm that the recipient accepts an attorney-certified copy.",
      "boundary": "A true-copy certificate does not authenticate the issuing institution or replace an issuer-certified copy when required."
    },
    "notary-translation": {
      "documents": "Provide the source document, passport spelling and the recipient’s translator requirements.",
      "action": "Check names, dates, numbers and seals before using the required translator declaration or certification.",
      "boundary": "A notarized translation is not a substitute for a specifically required NAATI or sworn translator."
    },
    "notary-affidavit": {
      "documents": "Bring original ID, supporting facts and the recipient’s declaration form.",
      "action": "Confirm who may witness the declaration; draft only facts personally known to the declarant before signing.",
      "boundary": "An affidavit cannot replace an official certificate unless the recipient permits it; false statements carry legal risk."
    },
    "notary-corporate": {
      "documents": "Provide current company records, signing conditions, board authorization and the director’s original ID.",
      "action": "Check director authority and any seal or joint-signature conditions before certifying the signature or copy.",
      "boundary": "Certification does not validate the board decision or authorize the transaction under foreign law."
    },
    "notary-poa": {
      "documents": "Provide the principal’s ID, the agent’s details and the bank or property authority’s form.",
      "action": "Limit the powers to the intended transaction and arrange the principal’s personal signing as required.",
      "boundary": "A couriered signed form is not a substitute for appearing before the required witness."
    },
    "apostille-personal": {
      "documents": "Provide an issuer-certified birth, marriage or civil-status record and the recipient’s checklist.",
      "action": "Check the destination and filing date before choosing consular legalisation or an available Apostille route.",
      "boundary": "Do not order a Thai Apostille before entry into force; final eligibility depends on the treaty relationship and document type."
    },
    "apostille-education": {
      "documents": "Provide the diploma, transcript, institution certification and the receiving evaluator’s instructions.",
      "action": "Check whether the institution must send records directly; obtain the accepted original or certified copy before translation.",
      "boundary": "Legalisation is not qualification recognition and cannot replace a credential assessment."
    },
    "apostille-corporate": {
      "documents": "Provide current registrar-certified company or tax records and the requested translations.",
      "action": "Check company names, directors and registered details before choosing the applicable authentication route.",
      "boundary": "Authentication does not confirm creditworthiness or guarantee overseas business approval."
    },
    "apostille-inbound": {
      "documents": "Provide the foreign original, its origin-country authentication and the Thai recipient’s instructions.",
      "action": "Verify the origin-country chain first, then prepare Thai translation and any required translation legalisation.",
      "boundary": "A foreign Apostille alone does not establish acceptance in Thailand before the Convention applies between the countries; we cannot issue another country’s Apostille."
    },
    "mfa-birth-marriage": {
      "documents": "Provide the original or registry-certified birth or marriage record and passport name spelling.",
      "action": "Check the registrar’s seals and translated names, dates and places before MFA submission and any required embassy step.",
      "boundary": "Legalisation verifies the relevant signature or seal, not the underlying family rights or the recipient’s decision."
    },
    "mfa-single-status": {
      "documents": "Obtain the civil-status certificate from the district registrar; include divorce evidence where relevant.",
      "action": "Check the recipient’s document-age rule before translation, MFA filing and any required embassy attestation.",
      "boundary": "An attorney does not issue a government single-status certificate; a declaration is an alternative only if accepted."
    },
    "mfa-conduct": {
      "documents": "Provide the police-issued clearance, original ID and the recipient’s validity and submission rules.",
      "action": "Obtain police clearance first; confirm whether the recipient needs translation or additional legalisation.",
      "boundary": "The police determine the record and issue the certificate; a law firm cannot alter the result."
    },
    "mfa-education": {
      "documents": "Provide institution-issued qualifications, transcripts and the recipient’s certification requirements.",
      "action": "Obtain institution verification if required, then check translation and the MFA or embassy filing order.",
      "boundary": "Legalisation does not recognize a degree’s level or guarantee admission or employment."
    },
    "mfa-commercial": {
      "documents": "Provide the commercial document, signer’s company authority and the recipient’s checklist.",
      "action": "Distinguish public records from private contracts or invoices; confirm any issuer, chamber or notarial step before MFA.",
      "boundary": "Authentication is not a contract review or an import/export licence; commercial and customs certificates may follow separate rules."
    },
    "combo-full-chain": {
      "documents": "Provide the complete recipient checklist, originals, identity and any permitted authorization.",
      "action": "Group public and private documents, assign only necessary certification steps, then confirm translation, MFA and embassy requirements.",
      "boundary": "Not every file needs every step; receiving authorities retain the decision on acceptance."
    }
  },
  "zh": {
    "notary-signature": {
      "documents": "携带身份证件原件及尚未签署的完整文件。",
      "action": "先确认受理机构的见证用语，再由签署人到律师面前签名。",
      "boundary": "认证见证签署行为，不证明文件内容真实或法律效力。"
    },
    "notary-true-copy": {
      "documents": "提供原件及所有相关页的清晰副本。",
      "action": "先问受理机构是否接受律师认证副本，再逐页对照原件。",
      "boundary": "副本认证不证明签发机构真伪，也不能替代必须由签发机关认证的副本。"
    },
    "notary-translation": {
      "documents": "提供原文、护照姓名拼写及受理机构的译员要求。",
      "action": "核对姓名、日期、数字与印章，再按要求出具译员声明或认证。",
      "boundary": "普通公证翻译不能替代明确要求的 NAATI 或宣誓译员翻译。"
    },
    "notary-affidavit": {
      "documents": "携带身份证件原件、事实依据及受理机构的声明表。",
      "action": "先确认谁可见证；仅陈述声明人亲自知道的事实，再签署。",
      "boundary": "未经受理机构同意，宣誓书不能代替官方证明；虚假声明存在法律风险。"
    },
    "notary-corporate": {
      "documents": "提供现行公司登记、签署条件、董事授权及董事身份证件原件。",
      "action": "核对董事权限、公司印章及共同签署条件后，再认证签名或副本。",
      "boundary": "认证不证明董事决议有效，也不代表外国法律已批准交易。"
    },
    "notary-poa": {
      "documents": "提供委托人证件、代理人资料及银行或房产机构指定表格。",
      "action": "限定具体交易权限，并按要求安排委托人亲自签署。",
      "boundary": "邮寄已签表格不能替代在指定见证人面前签署。"
    },
    "apostille-personal": {
      "documents": "提供签发机关认证的出生、婚姻或身份记录及受理清单。",
      "action": "根据目的地与递交日期选择领事认证或可用的 Apostille 路径。",
      "boundary": "公约生效前不可申请泰国 Apostille；是否适用还须核对两国条约关系及文件类型。"
    },
    "apostille-education": {
      "documents": "提供毕业证、成绩单、学校认证及学历评估机构说明。",
      "action": "先问是否要求学校直接寄送，再取得被接受的原件或认证副本并翻译。",
      "boundary": "文件认证不是学历认可，不能替代学历评估。"
    },
    "apostille-corporate": {
      "documents": "提供现行登记机关认证的公司或税务记录及所需译文。",
      "action": "核对公司名称、董事与登记信息，再选择适用的认证路径。",
      "boundary": "认证不证明信用状况，也不保证海外商业许可。"
    },
    "apostille-inbound": {
      "documents": "提供外国原件、来源国认证及泰国受理机构要求。",
      "action": "先核对来源国认证链，再准备泰文译文及所需译文认证。",
      "boundary": "两国之间公约尚未适用时，外国 Apostille 本身不证明泰国必然接受；本所不能签发他国 Apostille。"
    },
    "mfa-birth-marriage": {
      "documents": "提供出生或婚姻登记原件或登记机关认证本及护照拼写。",
      "action": "核对登记员印章、译文姓名、日期与地点，再递交外交部及必要的使馆环节。",
      "boundary": "认证核实相关签名或印章，不裁定家庭权利或受理结果。"
    },
    "mfa-single-status": {
      "documents": "先向区县登记机关取得婚姻状况证明，曾离婚者准备离婚依据。",
      "action": "先确认文件有效期限，再翻译、递交外交部及必要的使馆认证。",
      "boundary": "律师不签发政府单身证明；声明仅在受理机构允许时可作替代。"
    },
    "mfa-conduct": {
      "documents": "提供警方签发的无犯罪记录证明、证件及受理机构的有效期要求。",
      "action": "先向警方申请，再确认是否需要翻译或额外认证。",
      "boundary": "记录与证明由警方决定和签发，律所不能更改结果。"
    },
    "mfa-education": {
      "documents": "提供学校签发的学历、成绩单及受理机构认证要求。",
      "action": "必要时先取得学校核验，再确认翻译与外交部、使馆递交顺序。",
      "boundary": "认证不认可学历等级，也不保证入学或就业。"
    },
    "mfa-commercial": {
      "documents": "提供商业文件、签署人的公司授权及受理清单。",
      "action": "区分公共记录与私人合同、发票；外交部之前先确认签发机关、商会或公证环节。",
      "boundary": "认证不是合同审查或进出口许可；商业及海关证明可能适用不同程序。"
    },
    "combo-full-chain": {
      "documents": "提供完整受理清单、原件、证件及允许的授权文件。",
      "action": "区分公共与私人文件，只安排必要的认证，再核对翻译、外交部与使馆要求。",
      "boundary": "并非所有文件需要每一步；是否接受仍由受理机构决定。"
    }
  },
  "ja": {
    "notary-signature": {
      "documents": "本人確認書類の原本と未署名の全書類をご用意ください。",
      "action": "提出先の認証文言を確認し、署名者が弁護士の面前で署名します。",
      "boundary": "認証は署名行為を証明するもので、内容の真実性や法的効力を保証しません。"
    },
    "notary-true-copy": {
      "documents": "原本と必要な全ページの鮮明な写しをご用意ください。",
      "action": "提出先が弁護士による写しの認証を認めるか確認し、原本と照合します。",
      "boundary": "原本の発行機関の真正を証明するものではなく、発行機関の認証が必要な場合は代替できません。"
    },
    "notary-translation": {
      "documents": "原文、旅券の氏名表記、提出先の翻訳者要件をご用意ください。",
      "action": "氏名、日付、数字、印章を確認し、指定された翻訳者宣言や認証を行います。",
      "boundary": "公証翻訳は、指定された NAATI や宣誓翻訳者の翻訳に代わるものではありません。"
    },
    "notary-affidavit": {
      "documents": "本人確認原本、事実の裏付け、指定された宣言書式をご用意ください。",
      "action": "立会人の資格を確認し、宣言者が直接知る事実のみを記載して署名します。",
      "boundary": "提出先が認めない限り公的証明の代わりにはならず、虚偽の宣言には法的リスクがあります。"
    },
    "notary-corporate": {
      "documents": "最新の会社登記、署名条件、取締役の権限資料と本人確認原本をご用意ください。",
      "action": "署名権限、社印、共同署名条件を確認してから署名や写しを認証します。",
      "boundary": "認証は取締役会決議の有効性や外国法上の取引許可を証明しません。"
    },
    "notary-poa": {
      "documents": "委任者の身分証、受任者情報、銀行や不動産機関の指定書式をご用意ください。",
      "action": "権限を対象取引に限定し、必要な対面署名を手配します。",
      "boundary": "署名済み書類の郵送は、指定立会人の面前での署名に代わりません。"
    },
    "apostille-personal": {
      "documents": "発行機関が認証した出生・婚姻・身分記録と提出先の一覧をご用意ください。",
      "action": "提出国と提出日を確認し、領事認証か利用可能なアポスティーユの手順を選びます。",
      "boundary": "タイでの条約発効前にアポスティーユを申請できません。両国間の条約適用と書類の種類も確認が必要です。"
    },
    "apostille-education": {
      "documents": "学位証、成績証明、学校の認証、評価機関の指示をご用意ください。",
      "action": "学校から直接送付が必要か確認し、受理される原本や認証写しを取得してから翻訳します。",
      "boundary": "書類認証は学歴認定ではなく、資格評価に代わりません。"
    },
    "apostille-corporate": {
      "documents": "最新の登記官認証会社・税務記録と必要な翻訳をご用意ください。",
      "action": "会社名、取締役、登記事項を確認して認証経路を選びます。",
      "boundary": "認証は信用力や海外での事業許可を保証しません。"
    },
    "apostille-inbound": {
      "documents": "外国原本、発行国での認証、タイ提出先の要件をご用意ください。",
      "action": "まず発行国の認証を確認し、タイ語訳と必要な訳文認証を準備します。",
      "boundary": "両国間で条約が適用される前は外国のアポスティーユだけでタイでの受理は決まりません。当所は他国のアポスティーユを発行できません。"
    },
    "mfa-birth-marriage": {
      "documents": "出生・婚姻記録の原本か登記官認証本と旅券の氏名表記をご用意ください。",
      "action": "印章、翻訳の氏名、日付、場所を照合し、外務省と必要な大使館手続に進みます。",
      "boundary": "認証は該当署名や印章を確認するもので、家族法上の権利や受理結果を決めません。"
    },
    "mfa-single-status": {
      "documents": "地区登録機関の婚姻状況証明を取得し、離婚歴があれば資料を追加します。",
      "action": "提出先の書類有効期間を確認してから翻訳、外務省、大使館の必要手続を行います。",
      "boundary": "弁護士は公的独身証明を発行しません。宣言書への代替は提出先が認める場合のみです。"
    },
    "mfa-conduct": {
      "documents": "警察発行の無犯罪証明、身分証、提出先の有効期間要件をご用意ください。",
      "action": "まず警察の証明を取得し、翻訳や追加認証が必要か確認します。",
      "boundary": "記録の判断と証明の発行は警察の権限で、法律事務所は結果を変更できません。"
    },
    "mfa-education": {
      "documents": "学校発行の学位・成績証明と提出先の認証要件をご用意ください。",
      "action": "必要な学校照会を先に行い、翻訳と外務省・大使館の提出順を確認します。",
      "boundary": "認証は学位水準の認定や入学・就職の保証ではありません。"
    },
    "mfa-commercial": {
      "documents": "商業書類、署名者の会社権限、提出先一覧をご用意ください。",
      "action": "公文書と私的契約・請求書を分け、外務省の前に発行機関・商工会議所・公証の要否を確認します。",
      "boundary": "認証は契約審査や輸出入許可ではなく、貿易・税関証明には別手続があり得ます。"
    },
    "combo-full-chain": {
      "documents": "提出先の全一覧、原本、本人確認書類、認められた委任をご用意ください。",
      "action": "公文書と私文書を分け、必要な認証のみを割り当て、翻訳・外務省・大使館要件を確認します。",
      "boundary": "すべての書類にすべての工程が必要とは限りません。受理は提出先が決定します。"
    }
  },
  "ko": {
    "notary-signature": {
      "documents": "신분증 원본과 서명하지 않은 전체 문서를 준비합니다.",
      "action": "수령 기관의 인증 문구를 확인한 뒤 서명자가 변호사 앞에서 직접 서명합니다.",
      "boundary": "인증은 서명 행위를 확인하며 내용의 진실성이나 법적 효력을 보증하지 않습니다."
    },
    "notary-true-copy": {
      "documents": "원본과 필요한 모든 페이지의 선명한 사본을 준비합니다.",
      "action": "수령 기관이 변호사 인증 사본을 인정하는지 확인하고 원본과 대조합니다.",
      "boundary": "사본 인증은 발급 기관의 진위를 확인하지 않으며 발급 기관 인증 사본이 필요한 경우 대체할 수 없습니다."
    },
    "notary-translation": {
      "documents": "원문, 여권 이름 표기와 수령 기관의 번역자 요건을 제공합니다.",
      "action": "이름, 날짜, 숫자와 인장을 확인하고 요구된 번역자 선언 또는 인증을 진행합니다.",
      "boundary": "공증 번역은 별도로 요구되는 NAATI 또는 선서 번역자의 번역을 대체하지 않습니다."
    },
    "notary-affidavit": {
      "documents": "신분증 원본, 사실을 뒷받침하는 자료와 지정 진술서를 준비합니다.",
      "action": "누가 진술을 입회할 수 있는지 확인하고 진술자가 직접 아는 사실만 작성해 서명합니다.",
      "boundary": "수령 기관의 허용 없이 공식 증명서를 대신할 수 없으며 허위 진술에는 법적 위험이 있습니다."
    },
    "notary-corporate": {
      "documents": "최신 회사 등록, 서명 조건, 이사회 권한과 이사의 신분증 원본을 준비합니다.",
      "action": "이사 권한, 회사 인장 및 공동서명 조건을 확인한 뒤 서명이나 사본을 인증합니다.",
      "boundary": "인증은 이사회 결의의 유효성이나 외국법상 거래 허가를 확인하지 않습니다."
    },
    "notary-poa": {
      "documents": "위임자의 신분증, 대리인 정보와 은행 또는 부동산 기관의 서식을 준비합니다.",
      "action": "권한을 해당 거래로 제한하고 필요한 직접 서명을 준비합니다.",
      "boundary": "서명한 서류의 우편 발송은 지정 입회자 앞에서 서명하는 절차를 대체하지 않습니다."
    },
    "apostille-personal": {
      "documents": "발급 기관이 인증한 출생, 혼인 또는 신분 기록과 수령 목록을 준비합니다.",
      "action": "목적국과 제출일에 따라 영사 인증 또는 이용 가능한 아포스티유 경로를 확인합니다.",
      "boundary": "태국에서 협약 발효 전에 아포스티유를 신청할 수 없습니다. 양국 간 적용 관계와 문서 종류를 확인해야 합니다."
    },
    "apostille-education": {
      "documents": "학위, 성적표, 학교 인증과 학력 평가 기관의 지침을 준비합니다.",
      "action": "학교 직접 발송 요건을 먼저 확인하고 허용되는 원본이나 인증 사본을 확보한 뒤 번역합니다.",
      "boundary": "문서 인증은 학력 인정이 아니며 자격 평가를 대체하지 않습니다."
    },
    "apostille-corporate": {
      "documents": "최신 등록 기관 인증 회사 또는 세무 기록과 필요한 번역을 제공합니다.",
      "action": "회사명, 이사와 등록 내용을 확인한 뒤 인증 경로를 선택합니다.",
      "boundary": "인증은 신용도나 해외 사업 승인을 보증하지 않습니다."
    },
    "apostille-inbound": {
      "documents": "외국 원본, 발급국 인증과 태국 수령 기관의 지침을 준비합니다.",
      "action": "발급국 인증 경로를 먼저 확인한 뒤 태국어 번역과 필요한 번역 인증을 진행합니다.",
      "boundary": "양국 간 협약 적용 전에는 외국 아포스티유만으로 태국 수리가 확정되지 않습니다. 다른 나라의 아포스티유를 발급할 수 없습니다."
    },
    "mfa-birth-marriage": {
      "documents": "출생 또는 혼인 기록 원본이나 등록 기관 인증본 및 여권 표기를 제공합니다.",
      "action": "등록 인장, 번역 이름, 날짜와 장소를 확인한 뒤 외교부와 필요한 대사관 절차를 진행합니다.",
      "boundary": "인증은 해당 서명이나 인장을 확인하며 가족법상 권리나 수리 여부를 결정하지 않습니다."
    },
    "mfa-single-status": {
      "documents": "지역 등록 기관의 혼인 상태 증명을 먼저 받고 이혼 이력이 있으면 증빙을 추가합니다.",
      "action": "수령 기관의 문서 유효기간을 확인한 뒤 번역, 외교부와 필요한 대사관 인증을 진행합니다.",
      "boundary": "변호사는 정부의 미혼 증명을 발급하지 않습니다. 선언문 대체는 수령 기관이 허용할 때만 가능합니다."
    },
    "mfa-conduct": {
      "documents": "경찰 발급 범죄경력 증명, 신분증과 수령 기관의 유효기간 요건을 제공합니다.",
      "action": "경찰 증명을 먼저 받은 뒤 번역이나 추가 인증이 필요한지 확인합니다.",
      "boundary": "경찰이 기록을 판단하고 증명서를 발급하며 법률사무소는 결과를 바꿀 수 없습니다."
    },
    "mfa-education": {
      "documents": "학교 발급 학위, 성적표와 수령 기관의 인증 요건을 제공합니다.",
      "action": "필요하면 학교 확인을 먼저 받고 번역과 외교부 또는 대사관 제출 순서를 확인합니다.",
      "boundary": "인증은 학위 수준 인정이나 입학·취업 보증이 아닙니다."
    },
    "mfa-commercial": {
      "documents": "상업 문서, 서명자의 회사 권한과 수령 목록을 준비합니다.",
      "action": "공적 기록과 사적 계약·송장을 구분하고 외교부 이전에 발급 기관, 상공회의소 또는 공증 요건을 확인합니다.",
      "boundary": "인증은 계약 검토나 수출입 허가가 아니며 무역·세관 증명은 별도 규칙이 적용될 수 있습니다."
    },
    "combo-full-chain": {
      "documents": "전체 수령 목록, 원본, 신분증과 허용되는 위임을 제공합니다.",
      "action": "공문서와 사문서를 분류해 필요한 인증만 배정한 뒤 번역, 외교부와 대사관 요건을 확인합니다.",
      "boundary": "모든 서류에 모든 단계가 필요한 것은 아니며 수리 여부는 수령 기관이 결정합니다."
    }
  },
  "vi": {
    "notary-signature": {
      "documents": "Chuẩn bị giấy tờ tùy thân gốc và toàn bộ văn bản chưa ký.",
      "action": "Xác nhận lời chứng theo yêu cầu nơi nhận, rồi người ký trực tiếp ký trước luật sư.",
      "boundary": "Chứng thực xác nhận hành vi ký, không bảo đảm sự thật hoặc hiệu lực pháp lý của nội dung."
    },
    "notary-true-copy": {
      "documents": "Cung cấp bản gốc và bản sao rõ của tất cả trang cần thiết.",
      "action": "Hỏi nơi nhận có chấp nhận bản sao do luật sư chứng thực không, rồi đối chiếu với bản gốc.",
      "boundary": "Chứng thực bản sao không xác minh cơ quan cấp và không thay bản sao do cơ quan cấp chứng thực khi được yêu cầu."
    },
    "notary-translation": {
      "documents": "Cung cấp văn bản nguồn, tên theo hộ chiếu và yêu cầu về người dịch.",
      "action": "Kiểm tra tên, ngày, số và dấu trước khi dùng lời xác nhận hoặc chứng thực được yêu cầu.",
      "boundary": "Dịch công chứng không thay cho người dịch NAATI hoặc người dịch tuyên thệ nếu nơi nhận chỉ định."
    },
    "notary-affidavit": {
      "documents": "Mang giấy tờ tùy thân gốc, chứng cứ sự việc và mẫu tuyên bố của nơi nhận.",
      "action": "Xác nhận người có thể làm chứng; chỉ soạn sự việc người tuyên bố trực tiếp biết trước khi ký.",
      "boundary": "Tuyên thệ không thay chứng nhận nhà nước nếu nơi nhận không cho phép; lời khai sai có rủi ro pháp lý."
    },
    "notary-corporate": {
      "documents": "Cung cấp đăng ký công ty hiện hành, điều kiện ký, ủy quyền và giấy tờ gốc của giám đốc.",
      "action": "Kiểm tra quyền ký, con dấu và điều kiện đồng ký trước khi chứng thực chữ ký hoặc bản sao.",
      "boundary": "Chứng thực không xác nhận hiệu lực nghị quyết hay cho phép giao dịch theo luật nước ngoài."
    },
    "notary-poa": {
      "documents": "Cung cấp giấy tờ người ủy quyền, thông tin người nhận quyền và mẫu ngân hàng hoặc cơ quan bất động sản.",
      "action": "Giới hạn quyền cho giao dịch cụ thể và bố trí ký trực tiếp theo yêu cầu.",
      "boundary": "Gửi bưu điện văn bản đã ký không thay cho việc ký trước người làm chứng được yêu cầu."
    },
    "apostille-personal": {
      "documents": "Cung cấp bản khai sinh, hôn nhân hoặc hộ tịch do cơ quan cấp chứng thực cùng checklist nơi nhận.",
      "action": "Kiểm tra nước đích và ngày nộp trước khi chọn hợp pháp hóa lãnh sự hoặc Apostille có thể áp dụng.",
      "boundary": "Không xin Apostille Thái trước ngày có hiệu lực; còn phải kiểm tra quan hệ điều ước và loại tài liệu."
    },
    "apostille-education": {
      "documents": "Cung cấp bằng, bảng điểm, xác nhận trường và hướng dẫn của cơ quan đánh giá.",
      "action": "Kiểm tra yêu cầu trường gửi trực tiếp; lấy bản gốc hoặc bản sao được chấp nhận trước khi dịch.",
      "boundary": "Hợp pháp hóa không phải công nhận văn bằng và không thay đánh giá trình độ."
    },
    "apostille-corporate": {
      "documents": "Cung cấp hồ sơ công ty hoặc thuế hiện hành được cơ quan đăng ký chứng thực và bản dịch cần thiết.",
      "action": "Kiểm tra tên công ty, giám đốc và thông tin đăng ký trước khi chọn đường xác thực.",
      "boundary": "Xác thực không xác nhận uy tín tín dụng hoặc bảo đảm cấp phép kinh doanh ở nước ngoài."
    },
    "apostille-inbound": {
      "documents": "Cung cấp bản gốc nước ngoài, xác thực tại nước cấp và yêu cầu của cơ quan Thái.",
      "action": "Kiểm tra chuỗi tại nước cấp trước, sau đó dịch tiếng Thái và chứng thực bản dịch khi cần.",
      "boundary": "Apostille nước ngoài không tự bảo đảm tiếp nhận tại Thái trước khi Công ước áp dụng giữa hai nước; chúng tôi không cấp Apostille của nước khác."
    },
    "mfa-birth-marriage": {
      "documents": "Cung cấp bản gốc hoặc bản sao hộ tịch chứng thực của khai sinh, hôn nhân và tên hộ chiếu.",
      "action": "Kiểm tra dấu, tên, ngày và địa điểm trong bản dịch trước khi nộp MFA và bước đại sứ quán cần thiết.",
      "boundary": "Hợp pháp hóa kiểm tra chữ ký hoặc dấu, không quyết định quyền gia đình hay kết quả tiếp nhận."
    },
    "mfa-single-status": {
      "documents": "Xin chứng nhận tình trạng hôn nhân từ cơ quan hộ tịch; bổ sung chứng cứ ly hôn nếu có.",
      "action": "Kiểm tra giới hạn tuổi giấy tờ trước khi dịch, nộp MFA và chứng thực đại sứ quán khi cần.",
      "boundary": "Luật sư không cấp chứng nhận độc thân của nhà nước; tuyên bố chỉ thay thế khi nơi nhận chấp thuận."
    },
    "mfa-conduct": {
      "documents": "Cung cấp lý lịch do cảnh sát cấp, giấy tờ tùy thân và yêu cầu thời hạn của nơi nhận.",
      "action": "Xin giấy từ cảnh sát trước; xác nhận có cần dịch hoặc hợp pháp hóa thêm không.",
      "boundary": "Cảnh sát quyết định hồ sơ và cấp giấy; hãng luật không thể thay đổi kết quả."
    },
    "mfa-education": {
      "documents": "Cung cấp bằng và bảng điểm do trường cấp cùng yêu cầu chứng thực của nơi nhận.",
      "action": "Lấy xác minh trường khi cần rồi kiểm tra dịch và thứ tự nộp MFA hoặc đại sứ quán.",
      "boundary": "Hợp pháp hóa không công nhận cấp độ bằng hoặc bảo đảm tuyển sinh, việc làm."
    },
    "mfa-commercial": {
      "documents": "Cung cấp tài liệu thương mại, thẩm quyền người ký và checklist nơi nhận.",
      "action": "Phân biệt hồ sơ công với hợp đồng, hóa đơn tư; xác nhận bước cơ quan cấp, phòng thương mại hoặc công chứng trước MFA.",
      "boundary": "Xác thực không phải rà soát hợp đồng hoặc giấy phép xuất nhập khẩu; chứng từ hải quan có thể theo quy trình riêng."
    },
    "combo-full-chain": {
      "documents": "Cung cấp toàn bộ checklist nơi nhận, bản gốc, giấy tờ tùy thân và ủy quyền được cho phép.",
      "action": "Phân nhóm công và tư, chỉ chọn bước chứng thực cần thiết rồi xác nhận dịch, MFA và đại sứ quán.",
      "boundary": "Không phải hồ sơ nào cũng cần mọi bước; cơ quan nhận quyết định việc chấp nhận."
    }
  },
  "id": {
    "notary-signature": {
      "documents": "Siapkan identitas asli dan seluruh dokumen yang belum ditandatangani.",
      "action": "Konfirmasikan redaksi penerima, lalu penandatangan hadir dan menandatangani di hadapan pengacara.",
      "boundary": "Sertifikasi menyaksikan penandatanganan, bukan menjamin kebenaran atau keabsahan isi."
    },
    "notary-true-copy": {
      "documents": "Sediakan asli dan salinan jelas semua halaman terkait.",
      "action": "Pastikan penerima menerima salinan tersertifikasi pengacara sebelum membandingkan dengan asli.",
      "boundary": "Sertifikasi salinan tidak membuktikan keaslian instansi penerbit dan tidak menggantikan salinan tersertifikasi penerbit bila diwajibkan."
    },
    "notary-translation": {
      "documents": "Sediakan sumber, ejaan nama paspor dan persyaratan penerjemah dari penerima.",
      "action": "Periksa nama, tanggal, angka dan stempel sebelum memakai pernyataan penerjemah atau sertifikasi yang diminta.",
      "boundary": "Terjemahan notarial tidak menggantikan penerjemah NAATI atau tersumpah yang secara khusus diwajibkan."
    },
    "notary-affidavit": {
      "documents": "Bawa identitas asli, bukti fakta dan formulir pernyataan penerima.",
      "action": "Pastikan siapa yang berwenang menyaksikan; tulis hanya fakta yang diketahui langsung oleh pembuat pernyataan.",
      "boundary": "Afidavit tidak menggantikan sertifikat resmi tanpa izin penerima; pernyataan palsu berisiko hukum."
    },
    "notary-corporate": {
      "documents": "Sediakan registrasi perusahaan terbaru, syarat tanda tangan, otorisasi dan identitas asli direktur.",
      "action": "Periksa kewenangan direktur, stempel serta syarat tanda tangan bersama sebelum sertifikasi.",
      "boundary": "Sertifikasi tidak mengesahkan keputusan direksi atau mengizinkan transaksi menurut hukum asing."
    },
    "notary-poa": {
      "documents": "Sediakan identitas pemberi kuasa, data penerima kuasa dan formulir bank atau instansi properti.",
      "action": "Batasi kuasa pada transaksi yang dituju dan atur penandatanganan pribadi sesuai ketentuan.",
      "boundary": "Mengirim formulir yang sudah ditandatangani tidak menggantikan kehadiran di hadapan saksi yang diwajibkan."
    },
    "apostille-personal": {
      "documents": "Sediakan akta lahir, perkawinan atau status sipil tersertifikasi penerbit dan daftar penerima.",
      "action": "Periksa tujuan serta tanggal pengajuan sebelum memilih legalisasi konsuler atau Apostille yang berlaku.",
      "boundary": "Jangan meminta Apostille Thailand sebelum berlaku; hubungan perjanjian kedua negara dan jenis dokumen perlu diperiksa."
    },
    "apostille-education": {
      "documents": "Sediakan ijazah, transkrip, sertifikasi institusi dan instruksi evaluator.",
      "action": "Periksa apakah institusi harus mengirim langsung; dapatkan asli atau salinan yang diterima sebelum penerjemahan.",
      "boundary": "Legalisasi bukan pengakuan kualifikasi dan tidak menggantikan penilaian kredensial."
    },
    "apostille-corporate": {
      "documents": "Sediakan catatan perusahaan atau pajak terbaru tersertifikasi registrator dan terjemahan yang diminta.",
      "action": "Periksa nama perusahaan, direktur serta data registrasi sebelum memilih jalur autentikasi.",
      "boundary": "Autentikasi tidak membuktikan kelayakan kredit atau menjamin izin usaha di luar negeri."
    },
    "apostille-inbound": {
      "documents": "Sediakan asli asing, autentikasi negara asal dan instruksi penerima di Thailand.",
      "action": "Verifikasi rantai negara penerbit dahulu, lalu siapkan terjemahan Thai serta legalisasi terjemahan bila diperlukan.",
      "boundary": "Apostille asing saja tidak memastikan penerimaan di Thailand sebelum Konvensi berlaku antara kedua negara; kami tidak dapat menerbitkan Apostille negara lain."
    },
    "mfa-birth-marriage": {
      "documents": "Sediakan asli atau akta lahir/perkawinan tersertifikasi registrator serta ejaan paspor.",
      "action": "Periksa stempel, nama, tanggal dan tempat dalam terjemahan sebelum pengajuan MFA dan tahap kedutaan bila perlu.",
      "boundary": "Legalisasi memverifikasi tanda tangan atau stempel terkait, bukan menentukan hak keluarga atau keputusan penerima."
    },
    "mfa-single-status": {
      "documents": "Dapatkan sertifikat status perkawinan dari registrator distrik; tambah bukti perceraian bila relevan.",
      "action": "Periksa batas usia dokumen sebelum terjemahan, pengajuan MFA dan pengesahan kedutaan bila diminta.",
      "boundary": "Pengacara tidak menerbitkan sertifikat lajang pemerintah; pernyataan hanya alternatif bila penerima mengizinkan."
    },
    "mfa-conduct": {
      "documents": "Sediakan surat kepolisian, identitas dan ketentuan masa berlaku dari penerima.",
      "action": "Peroleh surat kepolisian dahulu; pastikan kebutuhan terjemahan atau legalisasi tambahan.",
      "boundary": "Kepolisian menentukan catatan dan menerbitkan sertifikat; firma hukum tidak dapat mengubah hasil."
    },
    "mfa-education": {
      "documents": "Sediakan kualifikasi, transkrip terbitan institusi dan persyaratan penerima.",
      "action": "Dapatkan verifikasi institusi bila perlu, lalu periksa terjemahan dan urutan MFA atau kedutaan.",
      "boundary": "Legalisasi tidak mengakui tingkat gelar atau menjamin penerimaan kuliah maupun pekerjaan."
    },
    "mfa-commercial": {
      "documents": "Sediakan dokumen komersial, kewenangan perusahaan penandatangan dan daftar penerima.",
      "action": "Pisahkan catatan publik dari kontrak atau faktur privat; pastikan tahap penerbit, kamar dagang atau notarial sebelum MFA.",
      "boundary": "Autentikasi bukan tinjauan kontrak atau izin impor/ekspor; sertifikat dagang dan bea cukai dapat mengikuti aturan terpisah."
    },
    "combo-full-chain": {
      "documents": "Sediakan seluruh daftar penerima, asli, identitas dan kuasa yang diizinkan.",
      "action": "Kelompokkan dokumen publik dan privat, tetapkan hanya sertifikasi yang perlu, lalu pastikan terjemahan, MFA dan kedutaan.",
      "boundary": "Tidak setiap berkas perlu semua tahap; keputusan penerimaan tetap pada instansi penerima."
    }
  }
};
